import React from 'react';
import {
  ChevronRight as ChevronRightIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';

import type { ProviderSummary } from '../../types';

interface ClinicianTableRowProps {
  provider: ProviderSummary;
  onClick: (id: string, name?: string) => void;
}

export const ClinicianTableRow: React.FC<ClinicianTableRowProps> = ({ provider, onClick }) => {
  const theme = useTheme();

  const name = provider.provider_name || 'Unknown Clinician';
  const email = (provider as any).email || `${name.toLowerCase().replace(' ', '.')}@mockclinic.com`;

  // Mocked data for design matching as requested
  const revenue = "$0";
  const revenueTrend = "↑ 0.0%";
  const unsignedCount: number = 0;
  const rejectionRate = "0%";
  const rejectionTrend = "↓ 0.0%";
  const timeToSubmit = "0 days avg";
  const timeToSubmitLabel = "Great";

  return (
    <Box
      onClick={() => onClick(provider.user_id, name)}
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 1.5fr 1fr 1.2fr 1fr 1fr 1.2fr 48px',
        alignItems: 'center',
        px: 2,
        py: 2,
        bgcolor: 'background.paper',
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: 'pointer',
        '&:hover': {
          bgcolor: '#FBFBFF',
        },
      }}
    >
      <Box onClick={(e) => e.stopPropagation()}>
        <Checkbox size="small" />
      </Box>

      {/* Clinician Info */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
          {name}
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {email}
        </Typography>
      </Box>

      {/* Status */}
      <Box>
        <Chip
          label="Active"
          size="small"
          sx={{
            bgcolor: '#E8F5E9',
            color: '#2E7D32',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
          }}
        />
      </Box>

      {/* Collected Revenue */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{revenue}</Typography>
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500 }}>
          {revenueTrend}
        </Typography>
      </Box>

      {/* Unsigned */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={`${unsignedCount} Note${unsignedCount === 1 ? '' : 's'}`}
          size="small"
          variant="outlined"
          sx={{
            height: 24,
            fontSize: '0.75rem',
            color: 'text.secondary',
            borderColor: 'divider',
          }}
        />
        {unsignedCount > 0 && (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6366f1' }} />
        )}
      </Box>

      {/* Rejection Rate */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{rejectionRate}</Typography>
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500, display: 'flex', alignItems: 'center' }}>
          <TrendingDownIcon sx={{ fontSize: 14, mr: 0.2 }} /> {rejectionTrend}
        </Typography>
      </Box>

      {/* Time to Submit */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{timeToSubmit}</Typography>
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
          {timeToSubmitLabel}
        </Typography>
      </Box>

      <Box>
        <ChevronRightIcon sx={{ color: theme.palette.grey[400] }} />
      </Box>
    </Box>
  );
};

export const ClinicianTableHeader: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '48px 1.5fr 1fr 1.2fr 1fr 1fr 1.2fr 48px',
        alignItems: 'center',
        px: 2,
        py: 1.5,
        bgcolor: '#F9FAFB',
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: '12px 12px 0 0',
      }}
    >
      <Box>
        <Checkbox size="small" />
      </Box>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Clinicians
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Status
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Collected Revenue
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Unsigned
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Rejection Rate
      </Typography>

      <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
        Time to Submit
      </Typography>

      <Box />
    </Box>
  );
};

